import { useEffect, useState, useRef } from "react";
import { MDX } from "./mdx/tokens";
import { Sparkles, Signal, TrendingUp, ShieldAlert } from "lucide-react";
import { PhoneFrame } from "@/components/frames/DeviceFrames";
import ObondoBubble from "@/components/mocks/ObondoBubble";
import "./mdx/mdx-mocks.css";

/**
 * ObondoDashboardPreview — Actual Obondo Advanced dashboard components
 * rendered inside CSS/SVG device frames (mobile + desktop).
 *
 * Both mocks are live-animated (1.4s tick): prices jitter, KPIs breathe,
 * the tilt heatmap re-shuffles, the intercept counter counts up, and the
 * LIVE badges pulse — mirroring the previous AnimatedTerminalMock feel.
 */
export default function ObondoDashboardPreview({ isDark }) {
    // Rotating Obondo coaching line — cycles every 6s between EN / SW / SH.
    const OBONDO_LINES = [
        { line: "Set your safety stop at 15.20 — don't push it higher.",               variant: "positive" },
        { line: "38% of your money is in SCOM. Try 6,200 shares instead.",             variant: "negative" },
        { line: "You're sticking to your plan 12% better this week. Endelea vizuri.",  variant: "positive" },
    ];
    const [lineIdx, setLineIdx] = useState(0);
    useEffect(() => {
        const t = setInterval(() => setLineIdx((i) => (i + 1) % OBONDO_LINES.length), 6000);
        return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const cur = OBONDO_LINES[lineIdx];

    return (
        <div className="relative grid md:grid-cols-2 gap-6 md:gap-8 items-center justify-items-center w-full max-w-[720px] mx-auto" data-testid="obondo-preview">
            <MobileFrame isDark={isDark} />
            <DesktopFrame isDark={isDark} />

            {/* Floating Obondo coach — desktop only, sits between the two phones */}
            <div
                key={lineIdx}
                className="hidden md:block absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none z-10"
                style={{ maxWidth: 300 }}
                data-testid="hero-obondo-bubble"
            >
                <ObondoBubble line={cur.line} variant={cur.variant} testId="hero-obondo-bubble-inner" />
            </div>
        </div>
    );
}

/* ── Mobile frame · BeCo dashboard (live-animated) ──────── */
function MobileFrame({ isDark }) {
    const panelBg = isDark ? "#0F1524" : "#FFFFFF";
    const dim = isDark ? "text-[#9CA3AF]" : "text-[#6B7280]";

    // Live-tick state — jitters every 1.4s (matches AnimatedTerminalMock).
    const [beco,  setBeco]  = useState(84.2);
    const [alpha, setAlpha] = useState(0.88);
    const [sigma, setSigma] = useState(0.14);
    const [rows,  setRows]  = useState([
        { sym: "SCOM", px: 18.35, chg: +2.4 },
        { sym: "EQTY", px: 42.10, chg: +0.9 },
        { sym: "COMI", px: 9.72,  chg: -0.6 },
    ]);
    const [heatSeed, setHeatSeed] = useState(0);
    const [flashKey, setFlashKey] = useState(0);
    const prevBeco = useRef(84.2);

    useEffect(() => {
        const id = setInterval(() => {
            const nb = +(Math.max(78, Math.min(90, beco  + (Math.random() - 0.5) * 0.3))).toFixed(1);
            const na = +(Math.max(0.75, Math.min(0.95, alpha + (Math.random() - 0.5) * 0.02))).toFixed(2);
            const ns = +(Math.max(0.08, Math.min(0.25, sigma + (Math.random() - 0.5) * 0.015))).toFixed(2);
            prevBeco.current = beco;
            setBeco(nb); setAlpha(na); setSigma(ns);
            setRows((prev) => prev.map((r) => {
                const jitter = (Math.random() - 0.5) * 0.4;
                const px = +(Math.max(0.1, r.px + jitter)).toFixed(2);
                const chg = +(r.chg + jitter * 0.1).toFixed(1);
                return { ...r, px, chg };
            }));
            setHeatSeed((s) => s + 1);
            setFlashKey((k) => k + 1);
        }, 1400);
        return () => clearInterval(id);
    }, [beco, alpha, sigma]);

    const flashCls = beco >= prevBeco.current ? "mdx-flash-up" : "mdx-flash-down";

    return (
        <div className="md-mock-anchor" data-testid="preview-mobile">
            <PhoneFrame>
                <div className="flex flex-col w-full h-full" style={{ background: panelBg, margin: -14, padding: 0 }}>
                    {/* Trial banner */}
                    <div
                        className="px-4 py-2 flex items-center gap-2 text-white font-inter text-[10px] font-bold uppercase tracking-wider"
                        style={{ background: MDX.blueLowSolid }}
                        data-testid="preview-trial-banner"
                    >
                        <Sparkles className="h-3 w-3" />
                        Obondo 7-Day Free Trial Active
                        <span className="ml-auto flex items-center gap-1 text-[9px]">
                            <span className="mdx-live-dot" style={{ background: "#F9FAFB" }} />
                            LIVE
                        </span>
                    </div>

                    <div className="p-3 flex-1 overflow-y-auto">
                        {/* KPI Triad · flashes on each tick */}
                        <div className="grid grid-cols-3 gap-2" data-testid="preview-kpi-triad">
                            <MiniKpi key={`b-${flashKey}`} label="BeCo"  value={beco.toFixed(1)}   tone="blue"   isDark={isDark} flashCls={flashCls} testid="preview-kpi-beco" />
                            <MiniKpi key={`a-${flashKey}`} label="α_B"   value={alpha.toFixed(2)}  tone="blue"   isDark={isDark} flashCls={flashCls} testid="preview-kpi-alpha" />
                            <MiniKpi key={`s-${flashKey}`} label="σ_B"   value={sigma.toFixed(2)}  tone="orange" isDark={isDark} flashCls={flashCls} testid="preview-kpi-sigma" />
                        </div>

                        {/* AI Toast */}
                        <div
                            className="mt-3 p-2.5 rounded-lg flex items-start gap-2"
                            style={{ background: MDX.tint10, border: `1px solid ${MDX.tint15}` }}
                            data-testid="preview-ai-toast"
                        >
                            <Sparkles className="h-3 w-3 mt-0.5 shrink-0" style={{ color: MDX.blueLow }} />
                            <div className="text-[10px] font-roboto leading-snug" style={{ color: isDark ? "#F9FAFB" : "#090D16" }}>
                                <b>Obondo:</b> Execution discipline <b style={{ color: MDX.blueLow }}>+12%</b> on EGX/NSE trades.
                            </div>
                        </div>

                        {/* Tilt heatmap — reshuffles each tick */}
                        <div className="mt-3">
                            <div className={`text-[9px] font-inter font-bold uppercase tracking-wider mb-1 ${dim}`}>Behavioral tilt · 24h</div>
                            <div className="flex gap-0.5 h-3 rounded overflow-hidden" data-testid="preview-heatmap">
                                {Array.from({ length: 24 }).map((_, i) => {
                                    const t = ((i * 37 + heatSeed * 13) % 100) / 100;
                                    return (
                                        <div
                                            key={i}
                                            className="flex-1 transition-colors duration-500"
                                            style={{ background: interpBlueOrange(t) }}
                                        />
                                    );
                                })}
                            </div>
                        </div>

                        {/* Sparklines · price rows tick each 1.4s */}
                        <div className="mt-3 space-y-1.5">
                            {rows.map((row, i) => (
                                <div key={row.sym} className="flex items-center justify-between text-[10px] font-mono" data-testid={`preview-row-${i}`}>
                                    <span className="font-inter font-semibold">{row.sym}</span>
                                    <MiniSpark up={row.chg >= 0} />
                                    <span key={`px-${flashKey}-${i}`} className={`proportional-nums ${row.chg >= 0 ? "mdx-flash-up" : "mdx-flash-down"}`}>{row.px.toFixed(2)}</span>
                                    <span
                                        className="proportional-nums font-semibold"
                                        style={{ color: row.chg >= 0 ? MDX.blueLow : MDX.orangeHi }}
                                    >
                                        {row.chg >= 0 ? "+" : ""}{row.chg.toFixed(1)}%
                                    </span>
                                </div>
                            ))}
                        </div>

                        {/* 10-metric drawer preview */}
                        <div className="mt-3">
                            <div className={`text-[9px] font-inter font-bold uppercase tracking-wider mb-1.5 ${dim}`}>10-metric drawer</div>
                            <div className="space-y-1">
                                {["α_B,1","α_B,2","σ_B,1","σ_B,2"].map((k, i) => (
                                    <div key={k} className="flex items-center gap-2 text-[9px] font-mono">
                                        <span style={{ width: 30 }}>{k}</span>
                                        <div className="flex-1 h-1.5 rounded-full" style={{ background: isDark ? "#1F2937" : "#E5E7EB" }}>
                                            <div
                                                className="h-full rounded-full transition-all duration-1000"
                                                style={{
                                                    width: `${55 + i * 8 + (heatSeed % 5)}%`,
                                                    background: i >= 2 ? MDX.orangeHi : MDX.blueLowSolid,
                                                }}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </PhoneFrame>
        </div>
    );
}

/* ── Second phone · Live Obondo AI intercept mid-trade ───── */
function DesktopFrame({ isDark }) {
    const panelBg = isDark ? "#0F1524" : "#FFFFFF";
    const brd = isDark ? "#1F2937" : "#E5E7EB";
    const text = isDark ? "#F9FAFB" : "#090D16";
    const dim = isDark ? "text-[#9CA3AF]" : "text-[#6B7280]";

    // Live-tick: SCOM price jitters, concentration counts 24 → 38 in a loop,
    // latency jitters, suggested size drifts around 6,200.
    const [px, setPx] = useState(18.35);
    const [chg, setChg] = useState(2.4);
    const [conc, setConc] = useState(24);
    const [suggested, setSuggested] = useState(6200);
    const [latency, setLatency] = useState(74);
    const [flashKey, setFlashKey] = useState(0);

    useEffect(() => {
        const id = setInterval(() => {
            setPx((v) => +(Math.max(16, Math.min(21, v + (Math.random() - 0.5) * 0.25))).toFixed(2));
            setChg((v) => +(Math.max(-1, Math.min(4, v + (Math.random() - 0.5) * 0.2))).toFixed(1));
            // Concentration climbs from 24 to 38 then resets — 14 steps ~= 20s cycle.
            setConc((c) => (c >= 38 ? 24 : c + 1));
            setSuggested((s) => Math.max(5900, Math.min(6600, s + Math.round((Math.random() - 0.5) * 60))));
            setLatency((l) => Math.max(58, Math.min(120, l + Math.round((Math.random() - 0.5) * 8))));
            setFlashKey((k) => k + 1);
        }, 1400);
        return () => clearInterval(id);
    }, []);

    return (
        <div className="md-mock-anchor" data-testid="preview-desktop">
            <PhoneFrame>
                <div className="flex flex-col w-full h-full" style={{ background: panelBg, margin: -14, padding: 0 }}>
                    {/* Trial banner */}
                    <div
                        className="px-4 py-2 flex items-center gap-2 text-white font-inter text-[10px] font-bold uppercase tracking-wider"
                        style={{ background: MDX.blueLowSolid }}
                    >
                        <Sparkles className="h-3 w-3" />
                        Obondo 7-Day Free Trial Active
                    </div>

                    <div className="p-3 flex-1 overflow-y-auto">
                        {/* Order ticket header */}
                        <div className="flex items-center justify-between mb-2">
                            <span className="font-inter font-bold text-[13px]" style={{ color: text }}>Order Ticket</span>
                            <span className="inline-flex items-center gap-1 text-[9px] font-mono px-1.5 py-0.5 rounded" style={{ background: MDX.tint10, color: MDX.blueLow }}>
                                <span className="mdx-live-dot" />
                                LIVE
                            </span>
                        </div>

                        {/* Symbol + live price row */}
                        <div className="rounded-md p-2.5 mb-3" style={{ border: `1px solid ${brd}`, background: isDark ? "#090D16" : "#F9FAFB" }}>
                            <div className="flex items-center justify-between">
                                <div>
                                    <div className="font-inter font-bold text-[15px]" style={{ color: text }}>SCOM</div>
                                    <div className={`text-[10px] font-mono ${dim}`}>Safaricom · NSE</div>
                                </div>
                                <div className="text-right">
                                    <div key={`px-${flashKey}`} className={`font-inter font-bold proportional-nums ${chg >= 0 ? "mdx-flash-up" : "mdx-flash-down"}`} style={{ color: text }}>
                                        {px.toFixed(2)}
                                    </div>
                                    <div className="text-[10px] font-mono proportional-nums" style={{ color: chg >= 0 ? MDX.blueLow : MDX.orangeHi }}>
                                        {chg >= 0 ? "+" : ""}{chg.toFixed(1)}%
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Buy/Sell + quantity */}
                        <div className="grid grid-cols-2 gap-2 mb-3">
                            <div className="rounded-md p-2 text-center" style={{ background: MDX.tint10, border: `1px solid ${MDX.blueLow}` }}>
                                <div className="text-[9px] font-inter font-bold uppercase tracking-wider" style={{ color: MDX.blueLow }}>BUY</div>
                                <div className="font-mono text-[11px] proportional-nums mt-0.5" style={{ color: text }}>10,000 sh</div>
                            </div>
                            <div className="rounded-md p-2 text-center" style={{ border: `1px solid ${brd}` }}>
                                <div className={`text-[9px] font-inter font-bold uppercase tracking-wider ${dim}`}>SELL</div>
                                <div className={`font-mono text-[11px] proportional-nums mt-0.5 ${dim}`}>—</div>
                            </div>
                        </div>

                        {/* Obondo AI intercept · counting concentration */}
                        <div
                            className="rounded-md p-2.5 mb-3 flex items-start gap-2 mdx-warn-pulse"
                            style={{ background: MDX.warn10, border: `1px solid ${MDX.orangeHi}` }}
                        >
                            <ShieldAlert className="h-3.5 w-3.5 mt-0.5 shrink-0" style={{ color: MDX.orangeHi }} />
                            <div>
                                <div className="font-inter font-bold text-[10px] uppercase tracking-wider" style={{ color: MDX.orangeHi }}>
                                    Obondo intercept · σ_B,2
                                </div>
                                <div className="text-[10px] font-roboto leading-snug mt-0.5" style={{ color: text }}>
                                    Concentration <b key={`c-${conc}`} className="mdx-flash-down proportional-nums">{conc}%</b> on SCOM after this fill.
                                    Suggested size <b className="proportional-nums" style={{ color: MDX.blueLow }}>{suggested.toLocaleString()}</b> shares.
                                </div>
                            </div>
                        </div>

                        {/* Suggested adjustment · execute buttons */}
                        <div className="grid grid-cols-2 gap-2">
                            <button
                                className="rounded-md py-2 text-[10px] font-inter font-bold uppercase tracking-wider mdx-cta-bloom"
                                style={{ background: MDX.greenCtaSolid, color: "#fff" }}
                            >
                                Accept {suggested.toLocaleString()}
                            </button>
                            <button className="rounded-md py-2 text-[10px] font-inter font-bold uppercase tracking-wider" style={{ border: `1px solid ${brd}`, color: dim }}>
                                Override
                            </button>
                        </div>

                        {/* Live telemetry footer · latency jitters */}
                        <div
                            className="mt-3 p-2 rounded-lg flex items-center gap-2"
                            style={{ background: MDX.tint10, border: `1px solid ${MDX.tint15}` }}
                        >
                            <Signal className="h-3 w-3 shrink-0 animate-pulse" style={{ color: MDX.blueLow }} />
                            <div className="text-[9px] font-roboto" style={{ color: text }}>
                                Latency <b className="proportional-nums">{latency}ms</b> · 4 fills today · 0 slippage
                            </div>
                        </div>
                    </div>
                </div>
            </PhoneFrame>
        </div>
    );
}

/* ── helpers ───────────────────────────────────────────────── */
function MiniKpi({ label, value, tone, isDark, big, testid, flashCls }) {
    const color = tone === "orange" ? MDX.orangeHi : MDX.blueLow;
    return (
        <div
            className="rounded-md px-2 py-2"
            style={{
                background: tone === "orange" ? MDX.warn10 : MDX.tint10,
                border: `1px solid ${tone === "orange" ? MDX.warn15 : MDX.tint15}`,
            }}
            data-testid={testid}
        >
            <div className={`font-inter font-bold uppercase tracking-wider ${big ? "text-[10px]" : "text-[9px]"} ${isDark ? "text-[#9CA3AF]" : "text-[#6B7280]"}`}>{label}</div>
            <div className={`font-inter font-bold proportional-nums ${big ? "text-2xl" : "text-lg"} ${flashCls || ""}`} style={{ color }}>{value}</div>
        </div>
    );
}

function MiniSpark({ up }) {
    const points = up
        ? "0,10 6,7 12,8 18,4 24,5 30,2 36,3"
        : "0,3 6,5 12,4 18,7 24,6 30,9 36,8";
    return (
        <svg width="36" height="12" viewBox="0 0 36 12">
            <polyline
                fill="none"
                stroke={up ? MDX.blueLow : MDX.orangeHi}
                strokeWidth="1.5"
                points={points}
            />
        </svg>
    );
}

function interpBlueOrange(t) {
    // 0 → red (brand), 0.5 → grayMid, 1 → orangeHi
    const c1 = t <= 0.5 ? "#922529" : "#6B7280";
    const c2 = t <= 0.5 ? "#6B7280" : "#EA580C";
    const k = t <= 0.5 ? t / 0.5 : (t - 0.5) / 0.5;
    return mixHex(c1, c2, k);
}
function mixHex(a, b, k) {
    const p = (h) => [parseInt(h.slice(1,3),16), parseInt(h.slice(3,5),16), parseInt(h.slice(5,7),16)];
    const [r1,g1,b1] = p(a), [r2,g2,b2] = p(b);
    const r = Math.round(r1 + (r2 - r1) * k);
    const g = Math.round(g1 + (g2 - g1) * k);
    const bl = Math.round(b1 + (b2 - b1) * k);
    return `rgb(${r},${g},${bl})`;
}
